import { ReactNode } from "react";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import { Theme, alpha } from "@mui/material/styles";
import { IncomeExpenseReportFigures } from "../../types/income-expense-report.type";
import ProfitSparkline from "./ProfitSparkline";
import { FigureTones, Tone, missingDocumentTipOf } from "./incomeExpenseReport";
import {
  countText,
  isNegativeMoney,
  isNegativePercent,
  moneyText,
  percentText,
} from "./reportFormat";

const TONE_PALETTE: Record<Tone, "success" | "warning" | "error"> = {
  good: "success",
  warn: "warning",
  bad: "error",
};
const TONE_BACKGROUND_OPACITY = 0.14;
const PERCENT_DECIMALS = 2;
const WHOLE_PERCENT_DECIMALS = 0;
const DOT_SIZE = 7;
/** "ผล" with no target is left blank, never 0 (it would read as the whole of the sales). */
const BLANK_TEXT = "";

const figureCellSx = (tone: Tone | null, isNegative: boolean, isEstimate: boolean) => ({
  ...(tone && {
    bgcolor: (theme: Theme) => alpha(theme.palette[TONE_PALETTE[tone]].main, TONE_BACKGROUND_OPACITY),
    color: `${TONE_PALETTE[tone]}.dark`,
  }),
  ...(isEstimate && { fontStyle: "italic", color: "primary.dark" }),
  // A loss is told by its minus sign AND the critical colour, never by colour alone.
  ...(isNegative && { color: "error.main" }),
});

interface FigureCellProps {
  text: string;
  tone?: Tone | null;
  isNegative?: boolean;
  isEstimate?: boolean;
  /** Drawn before the figure: the warning dot or the sparkline. */
  adornment?: ReactNode;
}

function FigureCell({
  text,
  tone = null,
  isNegative = false,
  isEstimate = false,
  adornment,
}: Readonly<FigureCellProps>) {
  return (
    <TableCell align="right" sx={figureCellSx(tone, isNegative, isEstimate)}>
      {adornment}
      {text}
    </TableCell>
  );
}

function MissingDocumentDot({ tip }: Readonly<{ tip: string }>) {
  return (
    <Tooltip title={tip}>
      <Box
        component="span"
        role="img"
        aria-label={tip}
        sx={{
          display: "inline-block",
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: "50%",
          bgcolor: "warning.main",
          mr: 0.75,
          verticalAlign: "middle",
        }}
      />
    </Tooltip>
  );
}

interface FigureCellsProps {
  figures: IncomeExpenseReportFigures;
  tones: FigureTones;
  /** ประมาณการ: Sales and Manager are the estimate — italic, like their header badge. */
  isEstimate: boolean;
  /** จริง: the expense cell's dot for route-days with no approved daily document. */
  showsMissingDocument: boolean;
  /** Body rows only; the totals row has no sparkline. */
  profitTrend?: number[];
}

/** Every figure column after วันทำงาน / วันที่, in the prototype's order. */
export default function FigureCells({
  figures,
  tones,
  isEstimate,
  showsMissingDocument,
  profitTrend,
}: Readonly<FigureCellsProps>) {
  const missingTip = showsMissingDocument ? missingDocumentTipOf(figures) : null;
  const money = (value: number | null) => ({
    text: moneyText(value),
    isNegative: isNegativeMoney(value),
  });
  return (
    <>
      <FigureCell {...money(figures.target)} />
      <FigureCell {...money(figures.sales)} />
      <FigureCell
        text={figures.result === null ? BLANK_TEXT : moneyText(figures.result)}
        isNegative={isNegativeMoney(figures.result)}
        tone={tones.result}
      />
      <FigureCell {...money(figures.cost)} />
      <FigureCell {...money(figures.waste_cost)} />
      <FigureCell text={percentText(figures.waste_percent, PERCENT_DECIMALS)} tone={tones.waste} />
      <FigureCell {...money(figures.total_cost)} />
      <FigureCell {...money(figures.gross_profit)} />
      <FigureCell
        text={percentText(figures.gross_profit_percent, WHOLE_PERCENT_DECIMALS)}
        isNegative={isNegativePercent(figures.gross_profit_percent, WHOLE_PERCENT_DECIMALS)}
      />
      <FigureCell {...money(figures.sales_expense)} isEstimate={isEstimate} />
      <FigureCell {...money(figures.manager_expense)} isEstimate={isEstimate} />
      <FigureCell
        {...money(figures.expense_total)}
        adornment={missingTip && <MissingDocumentDot tip={missingTip} />}
      />
      <FigureCell text={percentText(figures.expense_percent, PERCENT_DECIMALS)} tone={tones.expense} />
      <FigureCell
        {...money(figures.profit)}
        tone={tones.profit}
        adornment={profitTrend && <ProfitSparkline trend={profitTrend} />}
      />
      <FigureCell
        text={percentText(figures.profit_percent, WHOLE_PERCENT_DECIMALS)}
        tone={tones.profit}
        isNegative={isNegativePercent(figures.profit_percent, WHOLE_PERCENT_DECIMALS)}
      />
      <FigureCell text={percentText(figures.fuel_percent, PERCENT_DECIMALS)} tone={tones.fuel} />
      <FigureCell text={countText(figures.bills_per_route_day)} tone={tones.bills} />
    </>
  );
}
